import React from "react";
import { DateTime } from "luxon";
import { pipe } from "fp-ts/lib/function";
import { array } from "fp-ts";
import { TCasesDayViewForm } from "../../../../../../domain/codecs/form/CasesForm";
import { TCasesDayViewWeekCountsForm } from "../../../../../../domain/codecs/form/CasesDayViewWeekCountsForm";
import { CRMColors } from "../../../../models/CRMColors";
import { CRMSpacing } from "../../../../models/CRMSpacing";
import { CRMFontSizes } from "../../../../models/CRMFontSizes";
import { THexColor } from "../../../../models/StringLiterals";
import { Background } from "../../../BuildingBlocks/Background";
import { SpacingColumn } from "../../../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../../BuildingBlocks/SpacingRow";
import { Text } from "../../../BuildingBlocks/Text";
import { ShineShape } from "../../../BuildingBlocks/ShineShape";
import { CRMProgressBar } from "../../../Simple/CRMProgressBar/CRMProgressBar";

// Structural rather than the Cases codec type so Sail Homes can drive the same strip with
// listing roadblocks. The task counts are optional because that side has roadblocks only,
// and a zeroed count is omitted from the split line rather than rendered as "0 tasks".
type TDayWeekStripForm = {
    status: TCasesDayViewWeekCountsForm["status"];
    children: {
        days: Array<{
            date: string;
            tasks_count?: number;
            roadblocks_count: number;
        }>;
        overdue: {
            reschedulable_tasks_count?: number;
            key_dates_count?: number;
            roadblocks_count: number;
        };
        cleared_today: {
            tasks_count?: number;
            roadblocks_count: number;
        };
    };
};

type TCRMDayWeekStripProps = {
    form: TDayWeekStripForm;
    dateFilter: TCasesDayViewForm["edited"]["filters"]["date"];
    viewingName: string | null;
    onPickDate: (isoDate: string) => void;
    onPickOverdue: () => void;
};

type TDayChip = {
    isoDate: string;
    label: string;
    tasksCount: number;
    roadblocksCount: number;
};

const londonToday = (): DateTime =>
    DateTime.utc().setZone("Europe/London").startOf("day");

const countsSplitLine = (tasksCount: number, roadblocksCount: number): string => {
    const parts: Array<string> = [];
    if (tasksCount > 0) {
        parts.push(tasksCount === 1 ? "1 task" : `${tasksCount} tasks`);
    }
    if (roadblocksCount > 0) {
        parts.push(roadblocksCount === 1 ? "1 roadblock" : `${roadblocksCount} roadblocks`);
    }
    return parts.join(" · ");
};

const StripChip = (props: {
    caption: string;
    count: number;
    countColor: THexColor;
    captionColor: THexColor;
    splitLine: string;
    isSelected: boolean;
    barPercent: number | null;
    barColor: THexColor;
    backgroundColor: THexColor;
    borderColor: THexColor;
    onClick: () => void;
}): JSX.Element => (
    <Background
        color={props.backgroundColor}
        borderDefinition={`1px solid ${props.isSelected ? CRMColors.PRIMARY_4 : props.borderColor}`}
        borderRadius="8px"
        padding={CRMSpacing.MEDIUM}
        cursor="pointer"
        onClick={props.onClick}
        width="100%"
    >
        <SpacingColumn spacing={CRMSpacing.TINY}>
            <Text
                size={CRMFontSizes.X_SMALL}
                weight={700}
                letterSpacing="0.7px"
                color={props.captionColor}
            >
                {props.caption}
            </Text>
            <Text size={28} weight={700} lineHeight={1.1} color={props.countColor}>
                {props.count}
            </Text>
            {props.barPercent !== null &&
                <CRMProgressBar
                    progress={props.barPercent}
                    fullBarColor={props.barColor}
                    emptyBarColor={CRMColors.NEUTRAL_10}
                    height="5px"
                />
            }
            {/* Overdue and Next week carry no day-load bar. Without this spacer they end
                up a bar + gap shorter than the day chips and the row's bottom edge goes
                ragged — the chips sit side by side, so they have to agree on height. */}
            {props.barPercent === null &&
                <Background height="5px" />
            }
            <Text size={CRMFontSizes.X_SMALL} weight={600} color={CRMColors.NEUTRAL_4}>
                {props.splitLine || " "}
            </Text>
        </SpacingColumn>
    </Background>
);

export const CRMDayWeekStrip = (props: TCRMDayWeekStripProps): JSX.Element => {
    const today = londonToday();
    const thisFriday = today.startOf("week").plus({ days: 4 });
    const nextMonday = today.startOf("week").plus({ weeks: 1 });

    const countsByDate: Record<string, { tasks: number; roadblocks: number }> = {};
    for (const day of props.form.children.days) {
        countsByDate[day.date] = { tasks: day.tasks_count ?? 0, roadblocks: day.roadblocks_count };
    }

    const dayChips: Array<TDayChip> = [];
    for (let day = today; day.valueOf() <= thisFriday.valueOf(); day = day.plus({ days: 1 })) {
        const isoDate = day.toISODate();
        dayChips.push({
            isoDate,
            label: day.hasSame(today, "day")
                ? `${day.toFormat("ccc")} · Today`
                : day.toFormat("ccc d"),
            tasksCount: countsByDate[isoDate]?.tasks ?? 0,
            roadblocksCount: countsByDate[isoDate]?.roadblocks ?? 0,
        });
    }

    const laterDays = pipe(
        props.form.children.days,
        array.filter((day) => day.date > thisFriday.toISODate()),
    );
    const laterTasksCount = laterDays.reduce((sum, day) => sum + (day.tasks_count ?? 0), 0);
    const laterRoadblocksCount = laterDays.reduce((sum, day) => sum + day.roadblocks_count, 0);

    const overdueTasksCount =
        (props.form.children.overdue.reschedulable_tasks_count ?? 0)
        + (props.form.children.overdue.key_dates_count ?? 0);
    const overdueRoadblocksCount = props.form.children.overdue.roadblocks_count;
    const overdueTotal = overdueTasksCount + overdueRoadblocksCount;

    const maxDayCount = Math.max(1, ...dayChips.map((chip) => chip.tasksCount + chip.roadblocksCount));

    const clearedCount =
        (props.form.children.cleared_today.tasks_count ?? 0)
        + props.form.children.cleared_today.roadblocks_count;
    const todayChip = dayChips.find((chip) => chip.isoDate === today.toISODate());
    const outstandingTodayCount = todayChip
        ? todayChip.tasksCount + todayChip.roadblocksCount
        : 0;

    const title =
        props.viewingName === null ? "Your week"
        : props.viewingName === "The team" ? "The team's week"
        : `${props.viewingName}'s week`;

    const isLoading = props.form.status === "submitting";

    const selectedIsoDate = props.dateFilter.type === "iso" ? props.dateFilter.date : null;

    return (
        <Background padding={`0px ${CRMSpacing.X_LARGE}`}>
            <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                {/* HEADER */}
                <SpacingRow justifyContent="space-between" alignItems="center">
                    <Text size={CRMFontSizes.MED} weight={700} color={CRMColors.NEUTRAL_INK}>
                        {title}
                    </Text>
                    <Text size={CRMFontSizes.SMALL} weight={600} color={CRMColors.NEUTRAL_2}>
                        {`${clearedCount} of ${clearedCount + outstandingTodayCount} cleared today`}
                    </Text>
                </SpacingRow>

                {isLoading &&
                    <ShineShape
                        backgroundColor={CRMColors.NEUTRAL_PAPER}
                        shineColor={CRMColors.NEUTRAL_12}
                        duration={1000}
                        width="100%"
                        height="90px"
                        borderRadius="8px"
                    />
                }

                {!isLoading &&
                    <SpacingRow
                        spacing={CRMSpacing.SMALL}
                        childSize="even-fill"
                    >
                        {/* OVERDUE */}
                        <StripChip
                            caption="OVERDUE"
                            count={overdueTotal}
                            countColor={overdueTotal > 0
                                ? CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0
                                : CRMColors.NEUTRAL_6}
                            captionColor={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_2}
                            splitLine={countsSplitLine(overdueTasksCount, overdueRoadblocksCount)}
                            isSelected={props.dateFilter.type === "overdue"}
                            barPercent={null}
                            barColor={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_8}
                            backgroundColor={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_12}
                            borderColor={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_8}
                            onClick={props.onPickOverdue}
                        />

                        {/* THIS WEEK'S REMAINING DAYS */}
                        {dayChips.map((chip) => {
                            const total = chip.tasksCount + chip.roadblocksCount;
                            const isHeavy = total >= maxDayCount * 0.8 && total >= 10;
                            return (
                                <StripChip
                                    key={chip.isoDate}
                                    caption={chip.label.toUpperCase()}
                                    count={total}
                                    countColor={CRMColors.NEUTRAL_INK}
                                    captionColor={chip.isoDate === today.toISODate()
                                        ? CRMColors.PRIMARY_2
                                        : CRMColors.NEUTRAL_4}
                                    splitLine={countsSplitLine(chip.tasksCount, chip.roadblocksCount)}
                                    isSelected={chip.isoDate === selectedIsoDate}
                                    barPercent={Math.round((total / maxDayCount) * 100)}
                                    barColor={isHeavy
                                        ? CRMColors.HIGHLIGHTS_PERTINENT_YELLOW_0
                                        : CRMColors.HIGHLIGHTS_POSITIVE_GREEN_2}
                                    backgroundColor={CRMColors.NEUTRAL_PAPER}
                                    borderColor={CRMColors.NEUTRAL_10}
                                    onClick={() => props.onPickDate(chip.isoDate)}
                                />
                            );
                        })}

                        {/* NEXT WEEK ROLLUP */}
                        <StripChip
                            caption="NEXT WEEK"
                            count={laterTasksCount + laterRoadblocksCount}
                            countColor={CRMColors.NEUTRAL_6}
                            captionColor={CRMColors.NEUTRAL_6}
                            splitLine={countsSplitLine(laterTasksCount, laterRoadblocksCount)}
                            isSelected={selectedIsoDate !== null && selectedIsoDate >= nextMonday.toISODate()}
                            barPercent={null}
                            barColor={CRMColors.NEUTRAL_8}
                            backgroundColor={CRMColors.NEUTRAL_PAPER}
                            borderColor={CRMColors.NEUTRAL_10}
                            onClick={() => props.onPickDate(nextMonday.toISODate())}
                        />
                    </SpacingRow>
                }
            </SpacingColumn>
        </Background>
    );
};
